export const DEFAULT_DEBOUNCE_TIME = 500;

// TODO rename this file into something more descriptive
export function initProps(): null;
export function initProps<ValueType = unknown>(
  propsObj: Record<string, unknown>,
  key: string,
): ValueType;
export function initProps<ValueType = unknown>(propsObj?: any, key?: any): any {
  if (propsObj) {
    return propsObj[key] as ValueType;
  }
  return null;
}

/**
 * **createObjectWithKey**_(key, value)_<br>
 * Creates a new object containing a single key value pair.
 * @param { String } key: Key value.
 * @param { Any } value: Value of the key.
 * @returns { Object } New object containing key value pair.
 */
export function createObjectWithKey<ValueType = any>(key?: string, value?: ValueType) {
  const obj: Record<string, ValueType | undefined> = {};
  if (key) {
    obj[key] = value;
  }
  return obj;
}

type DropdownOption<ValueType = any> = {
  value: ValueType;
  text: string;
  key: string;
};

/**
 * **createDropdownOption**_(value, text, key, otherOptions)_<br>
 * Creates a dropdown option object.
 * @param { Any } value: Value of option.
 * @param { String } text: Display text of option. Will use value if none is passed.
 * @param { String } key: Key of option. Will use value if none is passed.
 * @param { Object } otherOptions: Merge other dropdown options.
 * @returns { Object } New dropdown option.
 */
export function createDropdownOption<ValueType = any>(
  value: ValueType,
  text?: string,
  key?: string,
  otherOptions?: Record<string, unknown>,
): DropdownOption<ValueType> & Record<string, unknown> {
  return {
    value,
    text: text || `${value}`,
    key: key || `${value}`,
    ...otherOptions,
  };
}

/**
 * **capitalizeFirstLetter**_(string)_<br>
 * Capitalize the first character of a string.
 * @param { String } string: The string to proper case.
 * @returns { String } The string in proper case.
 */
export const capitalizeFirstLetter = (string = '') => {
  if (!string) {
    return string;
  }
  return `${string.charAt(0).toUpperCase()}${string.substr(1)}`;
};

/**
 * **properCase**_(string)_<br>
 * Capitalize the first character of a string and lower cases the rest.
 * @param { String } string: The string to proper case.
 * @returns { String } The string in proper case.
 */
export const properCase = (string = '') => {
  if (!string) {
    return string;
  }
  return `${string.charAt(0).toUpperCase()}${string.substr(1).toLowerCase()}`;
};

/**
 * **generateId**_(append)_<br>
 * Generates a random id.
 * @param { Object } append: Additional string to append to id.
 * @returns { String } The generated ID.
 */
export const generateId = (append = '') => {
  const randString = Math.random().toString(36).substring(2, 15);
  return `${append.replace(/ /g, '')}${randString}${Date.now()}`;
};

/**
 * **omitFromObject**_(object, props)_<br>
 * Removes given props from an object
 * @param { Object } object: The object to remove props from.
 * @param { Array } props: Props to remove from object. Can be passed in as a comma delimited list.
 * @return { Object } Object with props removed.
 */
export const omitFromObject = (
  object: Record<string, any> | null,
  ...props: string[] | string[][]
) => {
  if (object === null) {
    return {};
  }

  const filteredObject = object;
  const flattenedProps = ([] as string[]).concat(...props); // Flatten props to flat list array.
  flattenedProps.forEach((prop) => {
    delete filteredObject[prop];
  });

  return filteredObject;
};

/**
 * **arrayToString**_(array, key)_<br>
 * Given an array of objects, build a string from a key in each object.
 * e.g. arrayToString([{word: 'hello'}, {word: 'world'}], 'word') => helloworld;
 * @param { Array } array: List of object to get key from.
 * @param { String } key: The key to get value from each object.
 * @returns { String } The built string from the array.
 */
export const arrayToString = (array: Record<string, any>[], key: string) =>
  array.map((arr) => arr[key]).join('');

export type RegexObject = {
  regex: RegExp;
};

export type RegexAdditionObject = {
  regex: RegExp | string;
};

/**
 * **buildRegex**_(base, exp)_<br>
 * Function to build to a regex
 * @param { RegEx } base: base regex to add to.
 * @param { Array } exp: experssions to add.
 * @returns { RegEx } New regex.
 */
export const buildRegex = (base: RegexObject, exp: RegexAdditionObject[]) =>
  new RegExp(base.regex.source.replace(/[$]/g, `${arrayToString(exp, 'regex')}$`));

export type RegexFormat<T = RegExp> = {
  regex: T;
  formatString: string;
};

/**
 * **createFormat**_(regex, formatString)_<br>
 * Create a date/time format object { regex, formatString }
 * @param { String } regex: RegEx value for this format.
 * @param { String } formatString: Format string of this format.
 * @returns { Object } The date/time format object { regex, formatString }.
 */
export function createFormat<T = RegExp>(regex: T, formatString: string) {
  return {
    regex,
    formatString,
  };
}

/**
 * **isArray**_(item)_<br>
 * Simple array check.
 * @param { Any } item: Item to check if it's an array.
 * @returns { boolean } If the item is an array.
 */
export function isArray<T = unknown>(item: unknown): item is T[] {
  return typeof item === 'object' && Array.isArray(item);
}

/**
 * **isBoolean**_(item)_<br>
 * Simple boolean check.
 * @param { Any } item: Item to check if it's a boolean.
 * @returns { boolean } If the item is a boolean.
 */
export const isBoolean = (item: unknown): item is boolean => typeof item === 'boolean';

/**
 * **isObject**_(item)_<br>
 * Simple object check.
 * @param { Any } item: Item to check if it's an object.
 * @returns { boolean } If the item is an object.
 */
export function isObject<T = Record<string, any>>(item: unknown): item is T {
  return Boolean(item) && typeof item === 'object' && !Array.isArray(item);
}

/**
 * **isString**_(input)_<br>
 * Simple string check.
 * @param { Any } input: Input to check if it's a string.
 * @returns { boolean } If the input is a string.
 */
export const isString = (input: unknown): input is string =>
  Boolean(input) && typeof input === 'string';

/**
 * **isFunction**_(input)_<br>
 * Simple function check.
 * @param { Any } input: Input to check if it's a function.
 * @returns { boolean } If the input is a function.
 */
export function isFunction<FnType = () => void>(input: unknown): input is FnType {
  return Boolean(input) && typeof input === 'function';
}

/**
 * **replaceInArray**_(list, itemIndex, item)_<br>
 * Replace an item in an array.
 * @param { Array } list: Array to replace item.
 * @param { Number } itemIndex: Index to replace item.
 * @param { Any } item: Item to replace.
 * @returns { Array } A new array with replaced item.
 */
export function replaceInArray<T = unknown>(): T[];
export function replaceInArray<T = unknown>(list: T, itemIndex?: number, item?: T): T;
export function replaceInArray<T = unknown>(list: T[], itemIndex?: number, item?: T): T[];
export function replaceInArray(list: any = [], itemIndex?: any, item?: any): any {
  if (isArray(list) && list[itemIndex] !== undefined) {
    return list.map((listItem, index) => (index === itemIndex ? item : listItem));
  }
  return list;
}

/**
 * **insertInArray**_(list, itemIndex, item)_<br>
 * Inserts an item to a given array at a given index.
 * @param { Array } list: The array to insert the item to.
 * @param { Number } itemIndex: The index of the item to insert.
 * @param { Any } item: The item to insert.
 * @returns { Array } The updated array with removed index.
 */
export function insertInArray<T = unknown>(list: T, itemIndex?: number, item?: T): T;
export function insertInArray<T = unknown>(list: T[], itemIndex: number, item: T): T[];
export function insertInArray(list: any, itemIndex?: number, item?: any): any {
  if (isArray(list)) {
    return [...list.slice(0, itemIndex), item, ...list.slice(itemIndex)];
  }
  return list;
}

/**
 * **removeFromArray**_(list, itemIndex)_<br>
 * Removes an item of a given index from an array.
 * @param { Array } list: The array to remove the item from.
 * @param { Number } itemIndex: The index of the item to remove.
 * @returns { Array } The updated array with removed index.
 */
export function removeFromArray<T = unknown>(list: T, itemIndex?: number): T;
export function removeFromArray<T = unknown>(list: T[], itemIndex: number): T[];
export function removeFromArray(list: any, itemIndex?: number): any {
  if (isArray(list)) {
    return list.filter((item, index) => index !== itemIndex);
  }
  return list;
}

/**
 * **reorderArray**_(list, itemIndex, newIndex)_<br>
 * Reorders an item in an array.
 * @param { Array } list: Array to reorder item.
 * @param { Number } itemIndex: Index of item to reorder.
 * @param { Number } newIndex: New index to move item to.
 * @returns { Array } A new array with reordered item.
 */
export function reorderArray<T = unknown>(list: T, itemIndex?: number, newIndex?: number): T;
export function reorderArray<T = unknown>(list: T[], itemIndex: number, newIndex: number): T[];
export function reorderArray(list: any, itemIndex?: any, newIndex?: any): any {
  if (isArray(list) && list[itemIndex]) {
    const reorderedList = list.slice(0);
    const item = list[itemIndex];
    reorderedList.splice(itemIndex, 1);
    reorderedList.splice(newIndex, 0, item);
    return reorderedList;
  }
  return list;
}

/**
 * **convertToArray**_(item)_<br>
 * Converts an item to an array if not already an array.
 * @param { Any } item: The item to convert to array.
 * @returns { Array } Array with item.
 */
export function convertToArray<Type = unknown>(item: Type): Type[] {
  return isArray<Type>(item) ? item : [item];
}

/**
 * **arrayToObject**_(list, key)_<br>
 * Creates an object from an array.
 * @param { Array } list: The array to create an object from.
 * @param { String } key: The key value to get from each item to set as the key value in the object.
 * @param { String } value: If passed in, sets the value of the object key.
 * @returns { Object } New dropdown option.
 */
// export function arrayToObject<T = unknown>(list: T[], key: string, value: string): T[];
// export function arrayToObject<T = unknown>(list: T[], key: string, value: string): T[];
export function arrayToObject<T = unknown, V = string>(list: T[], key: string, value?: string) {
  const arrayObject: Record<string, V | Record<string, string | V>> = {};
  list.forEach((item) => {
    if (isObject<Record<string, string | V>>(item) && item[key]) {
      const objectKey = item[key] as string;
      const objectValue = value ? (item[value] as V) : (item as Record<string, string | V>);
      arrayObject[objectKey] = objectValue;
    }
  });
  return Object.assign({}, arrayObject);
}

/**
 * parseToNumber
 * Parse a string number to a number value if possible.
 * @param { String } value: The number to parse.
 * @returns { Number | String } The parsed value to number.
 */
export function parseToNumber(): undefined;
export function parseToNumber(value: null): null;
export function parseToNumber(value: string): number | string;
export function parseToNumber(value?: string | null): number | string | undefined | null {
  if (value && value.slice) {
    if (value.slice(-1) === '.') {
      return value;
    }
    if (value.slice(-1) === '0' && value.includes('.')) {
      const decimal = value.split('.')[1];
      const precision = decimal.length;
      return parseFloat(value).toFixed(precision);
    }
  }

  if (!value) {
    return value;
  }

  const parsedValue = parseFloat(value);
  return Number.isNaN(parsedValue) ? value : parsedValue;
}

/**
 * **deepMerge**_(target, ...sources)_<br>
 * Deep merge two objects.
 * @param { Object } target: Base object to merge.
 * @param { Objects } sources: Additional objects to merge into target.
 *                             Subsequent values will override values in target.
 * @returns { Object } The merged object.
 */
export function deepMerge(): void;
export function deepMerge<T = unknown>(target: T, ...sources: any[]): T;
export function deepMerge<T = unknown>(
  target: Record<string, T>,
  ...sources: Record<string, T>[]
): Record<string, T>;
export function deepMerge(target?: any, ...sources: any[]): any {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    });
  }
  return deepMerge(target, ...sources);
}

/**
 * **deepMergeNew**_(target, ...sources)_<br>
 * Deep merge two objects into a new object.
 * @param { Objects } target: Objects to merge together.
 * @param { Objects } sources: Additional objects to merge into target.
 *                             Subsequent values will override values in target.
 * @returns { Object } The new merged object.
 */
export function deepMergeNew(): void;
export function deepMergeNew<T = unknown>(target: T, ...sources: any[]): T;
export function deepMergeNew<T = unknown>(
  target: Record<string, T>,
  ...sources: Record<string, T>[]
): Record<string, T>;
export function deepMergeNew(target?: any, ...sources: any[]): any {
  return deepMerge({}, Object.assign({}, target), ...sources);
}

type NestedKeyValue<ValueType = string> = string | Record<string, ValueType>;
/**
 * **getNestedKeys**_(obj, showAll, showVal, arr, nestedKey)_<br>
 * Creates an array of keys in dot notation for a given object.<br>
 * for object { a: { b: { c: null }, d: 'foo' }, e: 'bar' }<br>
 * the function returns [ 'a.b.c', 'a.d', 'e' ] if showAll is false<br>
 * the function returns ['a', 'a.b', 'a.b.c', 'a.d', 'e'] if showAll is true]<br>
 * the function returns [{'a.b.c': null}, {'a.d': 'foo'}, {'e': 'bar'}] if showAll is false and showVal is true<br>
 * the function returns [{'a': { b: { c: null}}}, {'a.b': { c: null }}, {'a.b.c': null}, {'a.d': 'foo'}, {'e': 'bar'}] if showAll is false and showVal is true<br>
 *
 * @param { Object } obj: a nested Object
 * @param { Boolean } showAll: a Boolean for showing all levels of keys - if false only leaves will be displayed
 * @param { Boolean } showVal: a Boolean for showing values
 * @param { Array } arr: The array value when recursively called.
 * @param { String } nestedKey: The key value when recursively called.
 * @returns { Array } arr: Array of keys in dot notation or a collection of objects where key is in dot notation and value is value of nested object
 */
export function getNestedKeys<ValueType = string>(
  obj: string | string[],
  showAll?: boolean,
  showVal?: boolean,
  arr?: NestedKeyValue<ValueType | Record<string, unknown>>[],
): string[];
export function getNestedKeys(obj: Record<string, unknown>): string[];
export function getNestedKeys<ValueType = string>(
  obj: Record<string, unknown>,
  showAll?: boolean,
  showVal?: boolean,
  arr?: NestedKeyValue<ValueType | Record<string, unknown>>[],
  nestedKey?: string,
): NestedKeyValue<ValueType | Record<string, unknown>>[];
export function getNestedKeys(
  obj: any,
  showAll?: any,
  showVal?: any,
  arr?: any,
  nestedKey?: any,
): any {
  if (arr) {
    // check if it's an object with keys
    if (isObject(obj) && Object.keys(obj).length > 0) {
      Object.keys(obj).forEach((key) => {
        const fullKey = nestedKey && nestedKey.length > 0 ? `${nestedKey}.${key}` : key;
        if (showAll) {
          arr.push(showVal ? { [fullKey]: obj[key] } : fullKey);
        }
        getNestedKeys(obj[key], showAll, showVal, arr, fullKey);
      });
    } else if (!showAll && nestedKey && nestedKey.length > 0) {
      arr.push(showVal ? { [nestedKey]: obj } : nestedKey);
    }
  } else {
    // init
    const show = typeof showAll === 'undefined' ? false : showAll;
    const val = typeof showVal === 'undefined' ? false : showVal;
    return getNestedKeys(obj, show, val, [], '');
  }
  return arr;
}

type RecursiveNestedParams = {
  arrKeys: string[];
  res: Record<string, unknown>;
  val?: unknown;
};

const recursivelyGetNested = ({
  arrKeys,
  res,
  val,
}: RecursiveNestedParams): Record<string, unknown> => {
  if (arrKeys.length > 0) {
    const curr = arrKeys.pop() ?? '';

    // TODO move this
    const valOrObj = val === 'object' ? {} : val;
    const newRes: Record<string, unknown> = { [curr]: valOrObj || { ...res } };
    return recursivelyGetNested({ arrKeys, res: newRes });
  }

  return res;
};

type MergedParentParams = {
  rootKey: string;
  res: Record<string, unknown>;
  currObj: Record<string, unknown>;
};

const getMergedParentObject = ({ res, rootKey, currObj }: MergedParentParams) =>
  isObject(res[rootKey]) ? deepMergeNew(res, currObj) : { ...res, ...currObj };

export type NestedObjectType<T = unknown> = Record<string, T>;
type RecursiveNestedObject<T> = Record<string, T | NestedObjectType<T>>;
type DeeplyRecursiveObject<T> = RecursiveNestedObject<RecursiveNestedObject<T>>;

/**
 * **getNestedObject**_(obj, newVal)_<br>
 * Creates a nested object given an array or object of keys in dot notation.
 * @param { Object } obj: an Object or collection of objects with keys in dot notation
 * @param { String } newVal: an optional string to set every value to
 * @returns { Object } a nested object
 */
export function getNestedObject(obj: string | number): Record<string, never>;
export function getNestedObject<T = unknown>(obj: string[], newVal?: T): NestedObjectType<T>;
export function getNestedObject<T = unknown>(obj: NestedObjectType<T>[]): DeeplyRecursiveObject<T>;
export function getNestedObject<T = unknown>(obj: NestedObjectType<T>): DeeplyRecursiveObject<T>;
export function getNestedObject(
  obj: NestedObjectType | NestedObjectType[] | string[],
  newVal?: string,
): any;
export function getNestedObject(obj: any, newVal?: any): any {
  let res = {};
  if (isObject<NestedObjectType>(obj)) {
    // sort the keys from shortest to longest
    const sortedKeys = Object.keys(obj).sort((a, b) => a.length - b.length);
    sortedKeys.forEach((key) => {
      const subKeys = key.split('.');
      const rootKey = subKeys[0];
      const currObj = recursivelyGetNested({ arrKeys: subKeys, res: {}, val: obj[key] });
      res = getMergedParentObject({ res, rootKey, currObj });
    });
  } else if (isArray<NestedObjectType | string>(obj)) {
    // check if collection
    if (isObject<NestedObjectType>(obj[0])) {
      // sort the collection from keys of shortest length to longest
      const sortedCollection = obj.sort(
        (a, b) => Object.keys(a)[0].length - Object.keys(b)[0].length,
      );
      sortedCollection.forEach((keyVal) => {
        const subKeys = Object.keys(keyVal)[0].split('.');
        const rootKey = subKeys[0];
        const currObj = recursivelyGetNested({
          arrKeys: subKeys,
          res: {},
          val: Object.values(keyVal)[0],
        });
        res = getMergedParentObject({ res, rootKey, currObj });
      });
    } else {
      // sort the keys from shortest length to longest
      const sortedKeys = (obj as string[]).sort((a, b) => a.length - b.length);
      sortedKeys.forEach((key) => {
        const subKeys = key.split('.');
        const rootKey = subKeys[0];
        const currObj = recursivelyGetNested({ arrKeys: subKeys, res: {}, val: newVal });
        res = getMergedParentObject({ res, rootKey, currObj });
      });
    }
  }

  return res;
}

type RandomIntParams = {
  max?: number;
  min?: number;
};

/**
 * **generateRandomInteger**_(obj, number)_<br>
 * Generate a random integer between max and min.
 * @param { Object } obj: made up of max and min integers.
 * @returns { number } The random output between min and max.
 */
export const generateRandomInteger = ({ max = 1, min = 0 }: RandomIntParams = {}) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

/**
 * **generateQueryString**_(url)_<br>
 * Generate query string from url's query params
 * @param { Object } params: Query params object like {userId: 'bruce@batman.com'}
 * @returns { string } A url's search section like ?userId=bruce@batman.com
 */
export const generateQueryString = (params: Record<string, string | number | boolean>) => {
  let queryString = '';
  Object.keys(params).forEach((key) => {
    queryString += queryString ? '&' : '?';
    queryString += `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
  });
  return queryString;
};

/**
 * **humanizeCamelCase**_(string)_<br>
 * Turns a camel case string into a human-readable one
 * @param { string } string: Camel case string
 * @returns { string } Human readable string
 */
export const humanizeCamelCase = (string: string) =>
  string
    .split(/(?=[A-Z])/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');

/**
 * **device**
 * Object of device sizes
 */
export const device = {
  mobile: `screen and (max-width: 600px)`,
  tablet: `screen and (min-width: 600px) and (max-width: 992px)`,
  browser: `screen and (min-width: 992px)`,
};

/**
 * Checks if the element is a number
 */
export const isTypeNumber = (el: unknown): el is number => typeof el === 'number';

/**
 *  Checks if the element is a string
 */
export const isTypeString = (el: unknown): el is string => typeof el === 'string';
